import {
  Button,
  Conditional,
  Input,
  Modal,
  SearchSelect,
  Select,
  TextArea,
} from "@result/rui/components";
import { useForm, useNotifications } from "@result/rui/hooks";
import { ApiFilter, ApiPage } from "@result/rui/utils/api";
import { toHumanReadableDate } from "@result/rui/utils/date";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";

import BreedSearchSelect from "#/components/BreedSearchSelect.jsx";
import DataLayerSearchSelect from "#/components/data-layer/SearchSelect";
import { DocumentGenerationContext } from "#/context";
import * as DLD from "#/def/data-layer";
import { animalKindCount } from "#/model/AnimalSexSpeciesCodeList.js";
import ApiClient from "#/redux/api/ApiClient.js";
import { getBreedingProgramsBySpecies } from "#/redux/resource/BreedingProgramSlice.js";
import {
  generateDocument,
  getDocumentPlaceholders,
} from "#/redux/resource/DocumentSlice.js";
import { fetchKmgMid } from "#/redux/resource/KmgSubjectsSlice.js";
import { getTemplates } from "#/redux/resource/TemplateSlice.js";
import {
  fetchAnimalGroupBuckets,
  selectBucketsMerged,
} from "#/redux/resource/animalGroupBucketSlice.js";
import { findAnimals } from "#/redux/resource/animalSlice.js";
import { defaultAnimalLabel } from "#/utils/AnimalUtils.js";
import { downloadFile } from "#/utils/FileUtils.js";
import * as species from "#/utils/SpeciesScope.js";

export const DocumentGenerationAdditionalInputs = {
  BREEDER: { id: "BREEDER" },
  ADDITIONAL_BREEDER: { id: "ADDITIONAL_BREEDER" },
  BREEDING_ORGANISATION: { id: "BREEDING_ORGANISATION" },
  ANIMAL_SMALL_LIVESTOCK: { id: "ANIMAL_SMALL_LIVESTOCK" },
  ANIMAL_PIG: { id: "ANIMAL_PIG" },
  ANIMAL_EQUINE: { id: "ANIMAL_EQUINE" },
  MATING_LOG: { id: "MATING_LOG" },
  MATING_STATION: { id: "MATING_STATION" },
  MATING_STATION_INSPECTION: { id: "MATING_STATION_INSPECTION" },
  MATING_STATION_STALLION_INSPECTION: {
    id: "MATING_STATION_STALLION_INSPECTION",
  },
  DATE_FROM: { id: "DATE_FROM" },
  DATE_TO: { id: "DATE_TO" },
  VALID_DATE: { id: "VALID_DATE" },
  BREEDING_PROGRAM: { id: "BREEDING_PROGRAM" },
  SPECIES: { id: "SPECIES" },
  BREED: { id: "BREED" },
  ANIMAL_1: { id: "ANIMAL_1" },
  ANIMAL_2: { id: "ANIMAL_2" },
  HERD_1: { id: "HERD_1" },
  HERD_2: { id: "HERD_2" },
  PEDIGREE_BOOK: { id: "PEDIGREE_BOOK" },
  YEAR: { id: "YEAR" },
};

const initialState = {
  isActive: false,
  isLoading: false,
  showModal: true,
  format: "DOCX",
  requestData: {
    breederId: null,
    additionalBreederId: null,
    animalId: null,
    speciesCode: null,
    breedingOrganisationId: null,
    breedingProgramId: null,
    dateFrom: null,
    dateTo: null,
    validDate: null,
    breedId: null,
  },
};

const DocumentGeneration = ({ children }) => {
  const [options, setOptions] = useState(initialState);
  const dispatch = useDispatch();

  const showDocumentGenerationModal = ({
    templateCode,
    requestData = {},
    additionalInputs = [],
    showModal = true,
    format = "DOCX",
  }) => {
    setOptions({
      ...options,
      showModal,
      isActive: true,
      templateCode,
      requestData,
      additionalInputs,
      format,
    });
  };

  const onClose = () => {
    setOptions(initialState);
  };

  useEffect(() => {
    if (options.templateCode != null) {
      dispatch(
        getTemplates({
          page: new ApiPage().page(0).size(100),
          filter: new ApiFilter().likeCaseInsensitive(
            "sifra",
            options.templateCode
          ),
        })
      );
    }
  }, [options.templateCode]);

  useEffect(() => {
    if (
      options?.templateCode?.startsWith("ListZaOcen") &&
      options?.requestData?.speciesCode === "KOZA"
    ) {
      setOptions({ ...options, templateCode: "ListZaOcenKozli" });
    }
    if (
      options?.templateCode?.startsWith("ListZaOcen") &&
      options?.requestData.speciesCode === "OVCA"
    ) {
      setOptions({ ...options, templateCode: "ListZaOcenOvni" });
    }

    if (
      options?.templateCode?.startsWith("SeznamZivaliPoRejcih") &&
      options?.requestData.speciesCode === "OVCA"
    ) {
      setOptions({ ...options, templateCode: "SeznamZivaliPoRejcihOvce" });
    }
    if (
      options?.templateCode?.startsWith("SeznamZivaliPoRejcih") &&
      options?.requestData.speciesCode === "KOZA"
    ) {
      setOptions({ ...options, templateCode: "SeznamZivaliPoRejcihKoze" });
    }
  }, [options.templateCode, options.requestData.speciesCode]);

  return (
    <DocumentGenerationContext.Provider
      value={{
        showDocumentGenerationModal,
        onClose,
        options: options,
        setIsLoading: (isLoading) => {
          setOptions((prev) => ({ ...prev, isLoading }));
        },
        setRequestData: (requestData) => {
          setOptions({ ...options, requestData: requestData });
        },
      }}>
      {children}
      <Conditional condition={options.isActive}>
        <Conditional condition={options.templateCode == null}>
          <SelectTemplateModal />
        </Conditional>
        <Conditional condition={options.templateCode != null}>
          <DocumentPlaceholdersForm options={options} />
        </Conditional>
      </Conditional>
    </DocumentGenerationContext.Provider>
  );
};

const SelectTemplateModal = () => {
  const { onClose } = useContext(DocumentGenerationContext);
  const { t } = useTranslation();

  return (
    <Modal onClose={onClose} title={t("document.generateDocument")}></Modal>
  );
};

const DocumentPlaceholdersForm = () => {
  const { setRequestData, options, onClose } = useContext(
    DocumentGenerationContext
  );
  const { templateCode, requestData, additionalInputs = [] } = options;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { addErrorNotification } = useNotifications();

  const { isLoading: isGenerating } = useSelector(
    (state) => state.documents.generate
  );

  const templatesByCode = useSelector(
    (state) => state.templates.templates.byCode
  );

  const templateId = useMemo(() => {
    if (templatesByCode == null || templatesByCode[templateCode] == null) {
      return null;
    }
    return templatesByCode[templateCode].id;
  }, [templateCode, templatesByCode]);

  const {
    byTemplateId: placeholdersByTemplateCode,
    isLoading: isLoadingDocumentPlaceholders,
  } = useSelector((state) => state.documents.documentPlaceholders);

  const documentPlaceholders = useMemo(() => {
    return placeholdersByTemplateCode[templateId];
  }, [templateId, placeholdersByTemplateCode]);

  const onSubmit = (values) => {
    const dto = {
      placeholders:
        requestData?.placeholders ??
        documentPlaceholders?.map((documentPlaceholder) => {
          return {
            ...documentPlaceholder,
            vrednost: values[documentPlaceholder.sifra],
          };
        }),
      zivalId: requestData.animalId,
      rejecId: requestData.breederId,
      rejecId2: requestData.additionalBreederId,
      pasmaId: requestData.breedId,
      speciesSifra: requestData.speciesCode,
      rejskaOrganizacijaId: requestData.breedingOrganisationId,
      rejniProgramId: requestData.breedingProgramId,
      datumOd: requestData.dateFrom,
      datumDo: requestData.dateTo,
      datumVeljavnosti: requestData.validDate,
      ...requestData,
    };
    dispatch(
      generateDocument({
        templateId,
        dto,
        format: options?.format ?? requestData.format ?? "DOCX",
      })
    )
      .unwrap()
      .then((fileData) => {
        downloadFile(fileData.data, fileData.fileName);
        onClose();
      })
      .catch((error) => {
        console.error("Error generating document:", error);
        addErrorNotification(
          t(`notification.generateDocumentError`),
          t("notification.error")
        );
      });
  };

  const validationSchema = documentPlaceholders?.reduce((acc, p) => {
    acc[p.sifra] = p.required
      ? yup.string().required()
      : yup.string().optional();
    return acc;
  }, {});

  const { getField, submit } = useForm({
    initialValues: {},
    schema: validationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (templateId != null) {
      dispatch(getDocumentPlaceholders(templateId));
    }
  }, [templateId]);

  useEffect(() => {
    if (templateId != null && !options.showModal) {
      onClose();
      onSubmit({});
    }
  }, [templateId, options.requestData]);

  useEffect(() => {
    if (documentPlaceholders?.length === 0 && additionalInputs?.length === 0) {
      onClose();
      onSubmit({});
    }
  }, [documentPlaceholders?.length, additionalInputs?.length]);

  if (
    documentPlaceholders == null ||
    (documentPlaceholders?.length === 0 && additionalInputs?.length === 0)
  ) {
    return null;
  }

  return (
    <form onSubmit={submit}>
      {
        <Conditional condition={isLoadingDocumentPlaceholders}>
          <Button color="white" isLoading={true} />
        </Conditional>
      }
      <Modal
        onClose={onClose}
        title={t("document.documentPlaceholders")}
        footerElement={
          <div className="buttons is-right is-flex-grow-1">
            <Button
              label={t("form.cancel")}
              onClick={onClose}
              isLoading={isGenerating || options.isLoading}
            />

            <Button
              label={t("form.generateDocument")}
              isLoading={isGenerating || options.isLoading}
              isSubmit={true}
              color="primary"
              onClick={submit}
            />
          </div>
        }>
        <div className="is-flex is-flex-direction-column is-gap-2">
          {documentPlaceholders?.length === 0 &&
            additionalInputs?.length === 0 && (
              <div className="has-text-grey">
                {t("templates.noPlaceholdersForTemplate")}
              </div>
            )}
          {additionalInputs?.map((input) => {
            switch (input?.id) {
              case DocumentGenerationAdditionalInputs.BREEDER.id:
                return (
                  <BreederSelect
                    fieldName="breederId"
                    label={t("form.breederCode")}
                  />
                );
              case DocumentGenerationAdditionalInputs.ADDITIONAL_BREEDER.id:
                return (
                  <BreederSelect
                    fieldName="additionalBreederId"
                    label={`${t("form.breederCode")} 2`}
                  />
                );
              case DocumentGenerationAdditionalInputs.BREEDING_ORGANISATION.id:
                return (
                  <BreederSelect
                    fieldName="breedingOrganisationId"
                    label={t("form.breedingOrganisation")}
                    extraFilters={(filter) => {
                      filter.equal("rejskaOrganizacija", true);

                      // Currently the setting of this filters doesn't work properly. As much as I can see now, so later we can filter out subjects even more
                      // based on which application they are used to.

                      /*if (species.has(species.HORSES)) {
                        filter.equal("dostopKonji", true);
                      } else if (species.has(species.PIGS)) {
                        filter.equal("dostopPrasici", true);
                      } else if (species.has(species.SHEEPS)) {
                        filter.equal("dostopOvce", true);
                      } else if (species.has(species.GOATS)) {
                        filter.equal("dostopKoze", true);
                      }*/
                    }}
                  />
                );
              case DocumentGenerationAdditionalInputs.ANIMAL_SMALL_LIVESTOCK.id:
              case DocumentGenerationAdditionalInputs.ANIMAL_PIG.id:
              case DocumentGenerationAdditionalInputs.ANIMAL_EQUINE.id:
                return <AnimalSelect {...input} />;
              case DocumentGenerationAdditionalInputs.ANIMAL_1.id:
                return (
                  <AnimalSelect
                    label={`${t("form.animalId")} 1`}
                    onSelection={(animal) => {
                      setRequestData({
                        ...options.requestData,
                        zivali1: [animal.usesnaStevilka],
                        speciesSifra: animal.speciesSifra,
                        rejecId: animal.rejecId,
                      });
                    }}
                    extraFilters={(filter) => {
                      filter.isNotNull("usesnaStevilka");
                    }}
                  />
                );
              case DocumentGenerationAdditionalInputs.ANIMAL_2.id:
                return (
                  <AnimalSelect
                    label={`${t("form.animalId")} 2`}
                    onSelection={(animal) => {
                      setRequestData({
                        ...options.requestData,
                        zivali2: [animal.usesnaStevilka],
                      });
                    }}
                    extraFilters={(filter) => {
                      filter.isNotNull("usesnaStevilka");

                      if (options.requestData?.speciesSifra != null) {
                        filter.equal(
                          "speciesSifra",
                          options.requestData?.speciesSifra
                        );
                      }
                    }}
                  />
                );
              case DocumentGenerationAdditionalInputs.HERD_1.id:
                return (
                  <HerdSelect
                    onSelection={(animals) => {
                      const animalIds = animals
                        .filter((animal) => animal.usesnaStevilka != null)
                        .map((animal) => animal.usesnaStevilka);
                      setRequestData({
                        ...options.requestData,
                        zivali1: animalIds,
                      });
                    }}
                  />
                );
              case DocumentGenerationAdditionalInputs.HERD_2.id:
                return (
                  <HerdSelect
                    onSelection={(animals) => {
                      const animalIds = animals
                        .filter((animal) => animal.usesnaStevilka != null)
                        .map((animal) => animal.usesnaStevilka);
                      setRequestData({
                        ...options.requestData,
                        zivali2: animalIds,
                        rejecId: animals[0]?.rejecId,
                        globina: 10,
                      });
                    }}
                  />
                );
              case DocumentGenerationAdditionalInputs.PEDIGREE_BOOK.id:
                return <PedigreeBookSelect />;
              case DocumentGenerationAdditionalInputs.DATE_FROM.id:
                return (
                  <DateInput fieldName="dateFrom" label={t("form.dateFrom")} />
                );
              case DocumentGenerationAdditionalInputs.DATE_TO.id:
                return (
                  <DateInput fieldName="dateTo" label={t("form.dateTo")} />
                );
              case DocumentGenerationAdditionalInputs.VALID_DATE.id:
                return (
                  <DateInput
                    fieldName="validDate"
                    label={t("form.validDate")}
                  />
                );
              case DocumentGenerationAdditionalInputs.BREEDING_PROGRAM.id:
                return <BreedingProgramSelect />;
              case DocumentGenerationAdditionalInputs.SPECIES.id: // TODO document generation should know in which segment it is and based on that filter results accordingly
                return <SpeciesSelect />;
              case DocumentGenerationAdditionalInputs.BREED.id:
                return <BreedSelect />;
              case DocumentGenerationAdditionalInputs.YEAR.id:
                return <YearInput />;
              case DocumentGenerationAdditionalInputs.MATING_LOG.id:
                return <MatingLogSelect />;
              case DocumentGenerationAdditionalInputs.MATING_STATION.id:
                return <MatingStationSelect />;
              case DocumentGenerationAdditionalInputs.MATING_STATION_INSPECTION
                .id:
                return <MatingStationInspectionSelect />;
              case DocumentGenerationAdditionalInputs
                .MATING_STATION_STALLION_INSPECTION.id:
                return <MatingStationStallionInspectionSelect />;
            }
          })}
          {documentPlaceholders?.map((placeholder) => (
            <div key={placeholder?.sifra}>
              <Conditional condition={placeholder?.tip === "SHORT"}>
                <Input
                  label={placeholder.vrednost}
                  placeholder={placeholder.sifra}
                  formField={getField(placeholder.sifra)}
                  isDisabled={isGenerating}
                />
              </Conditional>
              <Conditional condition={placeholder.tip === "LONG"}>
                <TextArea
                  label={placeholder.vrednost}
                  placeholder={placeholder.sifra}
                  formField={getField(placeholder.sifra)}
                  isDisabled={isGenerating}
                />
              </Conditional>
            </div>
          ))}
        </div>
      </Modal>
    </form>
  );
};

const BreederSelect = ({ fieldName, label, extraFilters }) => {
  const dispatch = useDispatch();
  const { setRequestData, options } = useContext(DocumentGenerationContext);

  return (
    <SearchSelect
      label={label}
      isRequired={true}
      optionId="id"
      optionLabel={(option) => {
        const optionLabels = [option?.sifraRejec];
        if (species.has(species.GOATS) || species.has(species.SHEEPS)) {
          optionLabels.push(option?.sifraOvce);
          optionLabels.push(option?.sifraKoze);
        } else if (species.has(species.PIGS)) {
          optionLabels.push(option?.sifraPrasici);
        } else if (species.has(species.HORSES)) {
          optionLabels.push(option?.sifraKopitarji);
        }
        return [
          option.kmgMid,
          ...optionLabels,
          option.imeInPriimek,
          option.naziv,
        ]
          .filter(Boolean)
          .join(" - ");
      }}
      pageSize={10}
      getLoadingState={(state) => state.kmgSubjects.isLoading}
      getOptionsState={(state) => state.kmgSubjects.page?.content ?? []}
      getPageNumberState={(state) => state.kmgSubjects.page?.number ?? 0}
      selectNone={false}
      onSelection={(breeder) => {
        setRequestData({
          ...options.requestData,
          [fieldName]: breeder.id,
        });
      }}
      onFetchData={(page, filterString, sort) => {
        const filter = new ApiFilter();

        if (extraFilters) {
          extraFilters(filter);
        }

        if (filterString != null && filterString !== "") {
          filter.or((orGroup) => {
            orGroup.likeCaseInsensitive("sifraRejca", filterString);

            // orGroup.likeCaseInsensitive("email", filterString);
            // orGroup.likeCaseInsensitive("mobitel", filterString);
            // orGroup.likeCaseInsensitive("telefon", filterString);
            if (species.has(species.GOATS) || species.has(species.SHEEPS)) {
              orGroup.likeCaseInsensitive("sifraOvce", filterString);
              orGroup.likeCaseInsensitive("sifraKoze", filterString);
            } else if (species.has(species.PIGS)) {
              orGroup.likeCaseInsensitive("sifraPrasici", filterString);
            } else if (species.has(species.HORSES)) {
              orGroup.likeCaseInsensitive("sifraKopitarji", filterString);
            }
            if (!Number.isNaN(Number(filterString))) {
              orGroup.equal("kmgMid", Number(filterString) ?? 0);
            }
            orGroup.likeCaseInsensitive("imeInPriimek", filterString);
            orGroup.likeCaseInsensitive("naziv", filterString);
            // orGroup.likeCaseInsensitive("naslov.ulica", filterString);
            // orGroup.like("naslov.hisnaStevilka", filterString);
            // orGroup.likeCaseInsensitive(
            //   "naslov.naslovDrugaVrstica",
            //   filterString
            // );
            // orGroup.like("naslov.postnaStevilka", filterString);
          });
        }
        dispatch(fetchKmgMid({ page, filter, sort }));
      }}
    />
  );
};

// `includeBreeder` opts a template into sending the selected animal's breeder
// (rejecId) along with the animal. Documents opened from the animal card get
// rejecId from the horse itself; here the animal is picked inside the modal,
// so the breeder has to be derived from the selection.
const AnimalSelect = ({
  onSelection,
  label,
  extraFilters,
  filter,
  includeBreeder = false,
}) => {
  const { t } = useTranslation();
  const { setRequestData, options } = useContext(DocumentGenerationContext);
  const dispatch = useDispatch();

  return (
    <SearchSelect
      label={label ?? t("form.animalId")}
      isRequired={true}
      onSelection={(animal) => {
        onSelection
          ? onSelection(animal)
          : setRequestData({
              ...options.requestData,
              animalId: animal.id,
              speciesCode: animal.speciesSifra,
              breedId: animal.priznanaPasmaId,
              ...(includeBreeder ? { rejecId: animal.rejecId ?? null } : {}),
            });
      }}
      getOptionsState={(state) => state.animals.page?.content}
      getPageNumberState={(state) => state.animals.page?.number}
      getLoadingState={(state) => state.animals.isLoading}
      selectNone={false}
      onFetchData={(page, filterString, sort) => {
        const apiFilter = new ApiFilter();

        if (filter) {
          filter(apiFilter);
        }
        if (options.requestData?.breederId != null) {
          apiFilter.equal("rejecId", options.requestData?.breederId);
        }

        apiFilter.or((orGroup) => {
          species
            .getTranslated()
            .forEach((s) => orGroup.equal("speciesSifra", s));
        });
        if (filterString != null && filterString !== "") {
          apiFilter.or((orGroup) => {
            orGroup.likeCaseInsensitive(
              "usesnaStevilka",
              filterString,
              true,
              true
            );
            orGroup.likeCaseInsensitive("ime", filterString, true, true);
            orGroup.likeCaseInsensitive(
              "rodovniskaStevilka",
              filterString,
              true,
              true
            );
            orGroup.likeCaseInsensitive(
              "originalnaStevilka",
              filterString,
              true,
              true
            );
            orGroup.likeCaseInsensitive(
              "zacasnaStevilka",
              filterString,
              true,
              true
            );
            orGroup.likeCaseInsensitive(
              "rodovniskaKnjigaNaziv",
              filterString,
              true,
              true
            );
          });
        }
        if (extraFilters) {
          extraFilters(apiFilter);
        }
        dispatch(findAnimals({ page, filter: apiFilter, sort }));
      }}
      optionId="id"
      optionLabel={defaultAnimalLabel}
    />
  );
};

const DateInput = ({ fieldName, label }) => {
  const { setRequestData, options } = useContext(DocumentGenerationContext);

  return (
    <Input
      type="date"
      label={label}
      onChange={(value) => {
        setRequestData({
          ...options.requestData,
          [fieldName]: value === "" ? null : value,
        });
      }}
    />
  );
};

const BreedingProgramSelect = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setRequestData, options } = useContext(DocumentGenerationContext);
  const [breedingPrograms, setBreedingPrograms] = useState([]);

  useEffect(() => {
    const supportedSpecies = species.getTranslated();
    if (supportedSpecies.length > 0) {
      dispatch(
        getBreedingProgramsBySpecies({
          speciesList: options.requestData?.speciesCode
            ? [options.requestData?.speciesCode]
            : supportedSpecies,
        })
      )
        .unwrap()
        .then((breedingPrograms) => {
          setBreedingPrograms(breedingPrograms);
        });
    }
  }, [options.requestData?.speciesCode]);
  return (
    <Select
      label={t("form.breedingProgram")}
      isRequired={true}
      onSelection={(breedingProgram) => {
        setRequestData({
          ...options.requestData,
          breedingProgramId: breedingProgram.id,
          breedingOrganisationId: breedingProgram.odgovorniSubjektId,
        });
      }}
      options={breedingPrograms}
      selectNone={false}
      optionId="id"
      optionLabel={(option) =>
        [option.naziv, option.opis].filter(Boolean).join(" - ")
      }
    />
  );
};

const SpeciesSelect = () => {
  const { setRequestData, options } = useContext(DocumentGenerationContext);

  const { t } = useTranslation();
  return (
    <Select
      isRequired={true}
      label={t("form.species")}
      onSelection={(selectedSpecies) => {
        setRequestData({
          ...options.requestData,
          speciesCode: selectedSpecies.id,
        });
      }}
      optionId={"id"}
      optionKey="numericId"
      optionLabel={"label"}
      options={[
        { id: "KOZA", label: "Koza" },
        { id: "OVCA", label: "Ovca" },
      ]}
    />
  );
};

const BreedSelect = () => {
  const { setRequestData, options } = useContext(DocumentGenerationContext);

  return (
    <BreedSearchSelect
      onSelection={(breed) =>
        setRequestData({ ...options.requestData, breedId: breed.id })
      }
    />
  );
};

const PedigreeBookSelect = () => {
  const { setRequestData, options } = useContext(DocumentGenerationContext);
  const { t } = useTranslation();
  return (
    <DataLayerSearchSelect
      ident={DLD.RODOVNISKA_KNJIGA}
      optionId="id"
      label={t("pedigree")}
      optionLabel={(pedigreeBook) =>
        [pedigreeBook.naziv, pedigreeBook.sifra].filter(Boolean).join(" - ")
      }
      onSelection={(pedigreeBook) =>
        setRequestData({
          ...options.requestData,
          rodovniskaKnjigaId: pedigreeBook.id,
        })
      }
    />
  );
};

const HerdSelect = ({ onSelection }) => {
  const { setRequestData, options } = useContext(DocumentGenerationContext);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const buckets = useSelector(selectBucketsMerged);

  const bucketsFiltered = useMemo(() => {
    const requestSpecies = options.requestData?.speciesSifra;
    if (buckets == null) {
      return [];
    }
    return buckets.filter((b) =>
      requestSpecies == null
        ? species.getTranslated().includes(b.speciesSifra)
        : requestSpecies === b.speciesSifra
    );
  }, [options.requestData]);

  useEffect(() => {
    dispatch(fetchAnimalGroupBuckets());
  }, [dispatch]);

  const [isLoading, setIsLoading] = useState();

  return (
    <Select
      label={t("animal.herd")}
      options={bucketsFiltered}
      optionId="id"
      isLoading={isLoading}
      optionKey="numericId"
      optionTag={(b) => [
        [
          ["", b.rejecNaziv]
            .filter(Boolean)
            .join(t("inbreeding.bucket.breederPrefix", "Rejec: ")),
          ["", b.lastnikNaziv]
            .filter(Boolean)
            .join(t("inbreeding.bucket.ownerPrefix", "Lastnik: ")),
          ["", b.priznanaPasmaSifra]
            .filter(Boolean)
            .join(t("inbreeding.bucket.breedsPrefix", "Pasme: ")),
          [
            animalKindCount(
              {
                ...b,
                speciesSifra: b.speciesSifra,
                spolSifra: b.spolSifra,
                razvojniStadijSifra: b.razvojniStadijSifra ?? b.razvojniStadij,
              },
              t,
              { count: b.steviloZivali }
            ),
          ]
            .filter(Boolean)
            .join(" "),
        ]
          .filter(Boolean)
          .join(", "),
        "is-info",
      ]}
      optionLabel={(b) =>
        [
          ["", b.rejecNaziv]
            .filter(Boolean)
            .join(t("inbreeding.bucket.breederPrefix", "Rejec: ")),
          ["", b.lastnikNaziv]
            .filter(Boolean)
            .join(t("inbreeding.bucket.ownerPrefix", "Lastnik: ")),
          ["", b.priznanaPasmaSifra]
            .filter(Boolean)
            .join(t("inbreeding.bucket.breedsPrefix", "Pasme: ")),
          [
            animalKindCount(
              {
                ...b,
                speciesSifra: b.speciesSifra,
                spolSifra: b.spolSifra,
                razvojniStadijSifra: b.razvojniStadijSifra ?? b.razvojniStadij,
              },
              t,
              { count: b.steviloZivali }
            ),
          ]
            .filter(Boolean)
            .join(" "),
        ]
          .filter(Boolean)
          .join(", ")
      }
      highlightSearch={true}
      onSelection={async (herd) => {
        const filterFields = {
          priznanaPasmaId: herd.priznanaPasmaId,
          rejecId: herd.rejecId,
          lastnikId: herd.lastnikId,
          speciesSifra: herd.speciesSifra,
          statusSifra: herd.statusSifra,
          spolSifra: herd.spolSifra,
          razvojniStadijSifra: herd.razvojniStadijSifra,
        };
        const filter = new ApiFilter();
        Object.entries(filterFields).forEach(([key, value]) => {
          if (value != null) {
            filter.equal(key, value);
          }
        });

        try {
          setIsLoading(true);
          const response = await ApiClient.get(
            `/api/zival/zivalaggregate/page`,
            undefined,
            new ApiPage().page(0).size(1000),
            filter,
            null,
            false,
            true
          );
          onSelection(response?.data?.content ?? []);
          setIsLoading(false);
        } catch (e) {
          console.error(e);
          setIsLoading(false);
        }
      }}
    />
  );
};

const YearInput = () => {
  const { t } = useTranslation();
  const { setRequestData, options } = useContext(DocumentGenerationContext);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setRequestData({
      ...options.requestData,
      datumDo: `${currentYear}-06-01`,
    });
  }, []);

  return (
    <Input
      type="number"
      label={t("form.year")}
      onChange={(value) => {
        setRequestData({
          ...options.requestData,
          datumDo: value === "" ? null : `${value}-06-01`,
        });
      }}
      value={options.requestData.datumDo?.split("-")[0] ?? ""}
    />
  );
};

const MatingLogSelect = () => {
  const { t } = useTranslation();
  const { setRequestData, options } = useContext(DocumentGenerationContext);

  return (
    <DataLayerSearchSelect
      ident={DLD.MATTING_LOGS}
      label={t("form.matingLog")}
      optionLabel={(pripust) => {
        let pripustLabel = [
          [
            pripust.datumZacetkaPripusta
              ? toHumanReadableDate(pripust.datumZacetkaPripusta)
              : null,
            pripust.datumKoncaPripusta
              ? toHumanReadableDate(pripust.datumKoncaPripusta)
              : null,
          ]
            .filter(Boolean)
            .join(" - "),
          [
            "Žrebci: ",
            pripust.pripustSamci
              ?.map(
                (zreb) =>
                  zreb?.samec?.zivljenskaStevilka ??
                  zreb?.samec?.usesnaStevilka ??
                  zreb?.samec?.ime ??
                  zreb?.samec?.id
              )
              .join(", "),
          ]
            .filter(Boolean)
            .join(", "),
        ]
          .filter(Boolean)
          .join(", ");

        if (pripustLabel == null) {
          pripustLabel = pripust.id;
        }
        return pripustLabel;
      }}
      filter={() => {
        const filter = new ApiFilter();
        if (options.requestData?.animalId != null) {
          filter.or((orGroup) => {
            orGroup.equal(
              "pripustSamice.samica.id",
              options.requestData.animalId
            );
          });
        }
        return filter;
      }}
      onSelection={(matingLog) => {
        setRequestData({
          ...options.requestData,
          pripustId: matingLog?.id ?? null,
        });
      }}
    />
  );
};

const MatingStationSelect = () => {
  const { t } = useTranslation();
  const { setRequestData, options } = useContext(DocumentGenerationContext);

  return (
    <DataLayerSearchSelect
      ident={DLD.DOGODEK_KOPITAR_PRIPUST_POSTAJA}
      label={t("form.matingStation")}
      optionLabel={(option) =>
        option?.naziv ?? option?.lokacijaGmid ?? option?.id
      }
      onSelection={(matingStation) => {
        setRequestData({
          ...options.requestData,
          pripustnaPostajaId: matingStation?.id ?? null,
        });
      }}
    />
  );
};

const MatingStationInspectionSelect = () => {
  const { t } = useTranslation();
  const { setRequestData, options } = useContext(DocumentGenerationContext);

  return (
    <DataLayerSearchSelect
      ident={DLD.DOGODEK_KOPITAR_PRIPUST_POSTAJA_PREGLED}
      label={t("form.matingStationInspection")}
      optionLabel={(option) =>
        [
          option.datumOcenjevanja
            ? "Dat. ocenjevanja: " +
              toHumanReadableDate(option.datumOcenjevanja)
            : null,
          option.stevilkaOdlocbe
            ? `Št. odločbe: ${option.stevilkaOdlocbe}`
            : null,
        ]
          .filter(Boolean)
          .join(" - ")
      }
      filter={() => {
        const filter = new ApiFilter();
        if (options.requestData?.pripustnaPostajaId != null) {
          filter.equal(
            "pripustnaPostajaId",
            options.requestData.pripustnaPostajaId
          );
        }
        return filter;
      }}
      onSelection={(matingStation) => {
        setRequestData({
          ...options.requestData,
          pregledPripustnePostajeId: matingStation?.id ?? null,
        });
      }}
    />
  );
};

const MatingStationStallionInspectionSelect = () => {
  const { t } = useTranslation();
  const { setRequestData, options } = useContext(DocumentGenerationContext);

  return (
    <DataLayerSearchSelect
      ident={DLD.DOGODEK_KOPITAR_PRIPUST_POSTAJA_PREGLED_ZREBCA}
      label={t("form.matingStationStallionInspection")}
      optionLabel={(option) =>
        option.zival?.usesnaStevilka ??
        option.zival?.ime ??
        option.zival?.id ??
        option.id
      }
      filter={() => {
        const filter = new ApiFilter();
        if (options.requestData?.pregledPripustnePostajeId != null) {
          filter.equal(
            "pripustnaPostajaPregledId",
            options.requestData.pregledPripustnePostajeId
          );
        }
        return filter;
      }}
      onSelection={(matingStation) => {
        setRequestData({
          ...options.requestData,
          pregledZrebcaId: matingStation?.id ?? null,
        });
      }}
    />
  );
};

export default DocumentGeneration;
